import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KeyframeIndex, ParsedShaderSet, PropertyKeyframes, PropertyKeyframeSet, PropIndex, ShaderId } from './parser/parser';

const TIME_WIDTH = 5;
const PROP_HEIGHT = 1.5;

// Time labels at the top of the timeline (0s, 1s, 2s, etc)
function TimeLabels(props: { maxTime: number }) {
  let res = [];
  for(let i = 0; i < props.maxTime; i++) {
    res.push(
      <div className="timeLabel" key={i}>{i}s</div>
    );
  }
  return <>{res}</>;
}

// A div containing keyframe control points
interface KeyframesContainerProps {
  maxTime: number;
  keyframes: PropertyKeyframes;
  setKeyframes: (keyframes: PropertyKeyframes) => void;

  activeKeyframe: number | null;
  setActiveKeyframe: (index: number | null) => void;
}

// Move the keyframe at index to a new time
function moveKeyframe(keyframes: PropertyKeyframes, index: number, time: number, val?: any): [PropertyKeyframes, number] {
  const res = [];
  let pushed = false;
  let resIndex = -1;
  const valToAdd = val === undefined ? keyframes[index].val : val;
  for(let i = 0; i < keyframes.length; i++) {
    if(i === index) continue;

    if(!pushed && time < keyframes[i].time) {
      resIndex = res.length;
      res.push({ val: valToAdd, time });
      pushed = true;
    }

    res.push(keyframes[i]);
  }

  if(!pushed) {
    resIndex = res.length;
    res.push({ val: valToAdd, time });
  }

  return [res, resIndex];
}

// Remove the keyframe at index
function removeKeyframe(keyframes: PropertyKeyframes, index: number): PropertyKeyframes {
  return [...keyframes.slice(0, index), ...keyframes.slice(index + 1)];
}

function editKeyframe(keyframes: PropertyKeyframes, index: number, val: any): PropertyKeyframes {
  return [
    ...keyframes.slice(0, index), 
    { val: val, time: keyframes[index].time },
    ...keyframes.slice(index + 1)
  ];
}

// Insert a keyframe at time t
function addKeyframe(keyframes: PropertyKeyframes, time: number, val: any): [PropertyKeyframes, number] {
  return moveKeyframe(keyframes, -1, time, val);
}

function editKeyframesSet(keyframes: PropertyKeyframeSet, shaderId: ShaderId, propIndex: PropIndex, propKeys: PropertyKeyframes): PropertyKeyframeSet {
  return {
    ...keyframes,
    keyframes: [
      ...keyframes.keyframes.slice(0, shaderId),
      [
        ...keyframes.keyframes[shaderId].slice(0, propIndex),
        propKeys,
        ...keyframes.keyframes[shaderId].slice(propIndex + 1)
      ],
      ...keyframes.keyframes.slice(shaderId + 1)
    ]
  };
}

export function insertKeyframeInSet(keyframes: PropertyKeyframeSet, shaderId: ShaderId, propIndex: PropIndex, time: number, val: any): [PropertyKeyframeSet, KeyframeIndex] {
  const [newKeyframes, keyframeIndex] = addKeyframe(keyframes.keyframes[shaderId][propIndex], time, val);
  return [editKeyframesSet(keyframes, shaderId, propIndex, newKeyframes), [shaderId, propIndex, keyframeIndex]];
}

export function removeKeyframeInSet(keyframes: PropertyKeyframeSet, index: KeyframeIndex): PropertyKeyframeSet {
  return editKeyframesSet(keyframes, index[0], index[1], removeKeyframe(keyframes.keyframes[index[0]][index[1]], index[2]));
}

export function editKeyframeInSet(keyframes: PropertyKeyframeSet, index: KeyframeIndex, val: any): PropertyKeyframeSet {
  return editKeyframesSet(keyframes, index[0], index[1], editKeyframe(keyframes.keyframes[index[0]][index[1]], index[2], val));
}

function KeyframesContainer(props: KeyframesContainerProps) {
  const { maxTime, keyframes, setKeyframes, activeKeyframe, setActiveKeyframe } = props;
  const [dragging, setDragging] = useState(false);
  const container = useRef<HTMLDivElement | null>(null);
  
  const onDrag = (event: React.MouseEvent) => {
    if(container.current === null || !dragging || activeKeyframe === null) return;

    const containerPos = container.current.getBoundingClientRect();
    const offset = Math.min(Math.max(((event.clientX - containerPos.left) / containerPos.width) * maxTime, 0), maxTime);
    
    const [newFrames, newActiveIndex] = moveKeyframe(keyframes, activeKeyframe, offset);
    setKeyframes(newFrames);
    setActiveKeyframe(newActiveIndex);
  };

  return (
    <div 
      className="keyframeContainer" 
      ref={container}
      style={{width: `${TIME_WIDTH * maxTime}rem`}}
      onMouseUp={(event) => {
        setDragging(false);
      }}
      onMouseLeave={(event) => { 
        setDragging(false);
      }}
      onMouseMove={onDrag}
    >
      {keyframes.map((key, index) => (
        <div
          key={index}
          className={`keyframe ${activeKeyframe === index ? "keyframeSelected" : ""}`}
          style={{left: `${key.time * TIME_WIDTH}rem`}}
          onMouseDown={(event) => {
            setActiveKeyframe(index);
            setDragging(true);
            event.stopPropagation();
          }}
        ></div>
      ))}
    </div>
  );
}

interface TimelineProps {
  maxTime: number;
  parsedShaders: ParsedShaderSet;
  activeShaders: ShaderId[];

  // Keyframes
  keyframes: PropertyKeyframeSet;
  setKeyframes: (keyframes: PropertyKeyframeSet) => void;

  // selected keyframe for editing
  activeKeyframe: KeyframeIndex | null;
  setActiveKeyframe: (key: KeyframeIndex | null) => void;

  // Current time in animation
  time: number;
  setTime: React.Dispatch<React.SetStateAction<number>>;
}

export default function Timeline(props: TimelineProps) {
  const {maxTime, keyframes, setKeyframes, parsedShaders, activeShaders, activeKeyframe, setActiveKeyframe, time, setTime} = props;

  const [draggingTime, setDraggingTime] = useState(false);
  const propsContainer = useRef<HTMLDivElement | null>(null);

  const [play, setPlay] = useState(false);
  const playCallbackId = useRef<number | null>(null);
  const lastPlayTime = useRef<DOMHighResTimeStamp>(performance.now());

  // start or stop animation
  const playHandler = useCallback((curTime: DOMHighResTimeStamp) => {
    const delta = (curTime - lastPlayTime.current) / 1000.0;
    setTime((time: number) => time >= maxTime ? 0.0 : time + delta);
    lastPlayTime.current = curTime;
    
    playCallbackId.current = requestAnimationFrame(playHandler);
  }, [setTime, maxTime]);

  const togglePlay = useCallback(() => {
    if(play) {
      setPlay(false);
      if(playCallbackId.current !== null) {
        window.cancelAnimationFrame(playCallbackId.current);
        playCallbackId.current = null;
      }
    } else {
      setPlay(true);
      lastPlayTime.current = performance.now();
      playHandler(lastPlayTime.current);
    }
  }, [play, playHandler]);

  // stop animation on unmount
  useEffect(() => {
    return () => {
      if(playCallbackId.current !== null)
        window.cancelAnimationFrame(playCallbackId.current)
    };
  }, []);

  // time dragging
  const onTimeDrag = useCallback((event: React.MouseEvent) => {
    if(propsContainer.current === null || !draggingTime) return;
    const box = propsContainer.current.getBoundingClientRect();
    const newTime = Math.max(0, Math.min(maxTime, maxTime * (event.clientX - box.left) / box.width));
    setTime(newTime);
    event.preventDefault();
  }, [maxTime, setTime, draggingTime]);

  // Display properties that have at least one keyframe
  const showProps: [ShaderId, PropIndex][] = [];
  for(let s = 0; s < parsedShaders.shaders.length; s++) {
    if(!activeShaders.includes(s)) continue;
    for(let p = 0; p < parsedShaders.shaders[s].properties.length; p++) {
      const keys = keyframes.keyframes[s][p];
      if(keys.length > 0) {
        showProps.push([s, p]);
      }
    }
  }

  const timeStyle = {left: `${time * TIME_WIDTH}rem`};

  return (
    <div className="timeline">
      <button
        onClick={togglePlay}
        className="playButton"
      >
        {play ? 
          <FontAwesomeIcon icon={faStop} />
          : 
          <FontAwesomeIcon icon={faPlay} />
        }
      </button>
      <div className="timelineLabels">
        <div className="timeCell">Time</div>
        {showProps.map(([shaderId, propIndex]) => {
          const shader = parsedShaders.shaders[shaderId];
          return (
            <div className="propLabel" key={shaderId * 1000 + propIndex}>
              {shader.name} {">"} {shader.properties[propIndex].displayName}
            </div>
          );
        })}
      </div>
      <div
        ref={propsContainer}
        className="timelineProps" 
        style={{height: `${(showProps.length + 1) * PROP_HEIGHT}rem`}}
        onMouseUp={event => setDraggingTime(false)}
        onMouseLeave={event => setDraggingTime(false)}
        onMouseMove={onTimeDrag}
        onMouseDown={event => setActiveKeyframe(null)}
      >
        <div 
          className="timeBar"
          style={timeStyle}
        />
        <div 
          className="timeBarHandle"
          style={timeStyle}
          onMouseDown={event => {
            setDraggingTime(true);
            event.stopPropagation();
          }}
        />
        <div className="timeLabels">
          <TimeLabels maxTime={maxTime} />
        </div>
        {showProps.map(([shaderId, propIndex]) => (
          <KeyframesContainer 
            key={shaderId * 1000 + propIndex}
            maxTime={maxTime} 
            keyframes={keyframes.keyframes[shaderId][propIndex]}
            setKeyframes={(propKeys) =>
              setKeyframes(editKeyframesSet(keyframes, shaderId, propIndex, propKeys))
            }
            activeKeyframe={
              activeKeyframe !== null && 
              shaderId === activeKeyframe[0] && 
              propIndex === activeKeyframe[1] ? 
                activeKeyframe[2] : null
            }
            setActiveKeyframe={(index) => {
              if(index === null)
                setActiveKeyframe(null);
              else
                setActiveKeyframe([shaderId, propIndex, index]);
            }}
          />
        ))}
      </div>
    </div>
  );
}